import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Trash2, ArrowLeft, ChevronRight, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const resolveName = (nameObj, lang) =>
  nameObj?.[lang] ?? nameObj?.['es'] ?? nameObj?.['en'] ?? '';

// ── Two-level category/subcategory picker (reusable) ────────────────────────
const CategorySubcategoryPicker = ({ onSelect, onCancel }) => {
  const { i18n, t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [selectedCat, setSelectedCat] = useState(null);
  const [loadingCats, setLoadingCats] = useState(true);
  const [loadingSubs, setLoadingSubs] = useState(false);

  useEffect(() => {
    axios.get(`${API}/categories`)
      .then(r => setCategories(r.data))
      .catch(() => toast.error('Failed to load categories'))
      .finally(() => setLoadingCats(false));
  }, []);

  const selectCategory = async (cat) => {
    setSelectedCat(cat);
    setLoadingSubs(true);
    try {
      const res = await axios.get(`${API}/categories/${cat.id}/subcategories`);
      setSubcategories(res.data);
    } catch {
      toast.error('Failed to load service types');
    } finally {
      setLoadingSubs(false);
    }
  };

  if (loadingCats) return <div className="py-8 text-center text-sm text-muted-foreground">Loading categories…</div>;

  if (!selectedCat) return (
    <div>
      <p className="text-sm font-medium mb-3">{t('service.select_category')}</p>
      <div className="grid grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1">
        {categories.map(cat => (
          <button
            key={cat.id}
            type="button"
            onClick={() => selectCategory(cat)}
            className="text-left px-3 py-2.5 rounded-lg border-2 border-gray-200 hover:border-primary text-sm font-medium transition-colors"
          >
            {resolveName(cat.name, i18n.language)}
          </button>
        ))}
      </div>
      <Button variant="ghost" size="sm" className="mt-4" onClick={onCancel}>Cancel</Button>
    </div>
  );

  return (
    <div>
      <div className="flex items-center gap-2 mb-3 text-sm">
        <button type="button" onClick={() => setSelectedCat(null)} className="text-primary hover:underline flex items-center gap-1">
          <ArrowLeft className="w-3.5 h-3.5" />
          {resolveName(selectedCat.name, i18n.language)}
        </button>
        <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
        <span className="text-muted-foreground">{t('service.select_subcategory')}</span>
      </div>
      {loadingSubs ? (
        <div className="py-6 text-center text-sm text-muted-foreground">Loading…</div>
      ) : (
        <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
          {subcategories.map(sub => (
            <button
              key={sub.id}
              type="button"
              onClick={() => onSelect({ category: selectedCat, subcategory: sub })}
              className="w-full text-left px-3 py-2.5 rounded-lg border-2 border-gray-200 hover:border-primary text-sm font-medium transition-colors"
            >
              {resolveName(sub.name, i18n.language)}
            </button>
          ))}
          {subcategories.length === 0 && (
            <p className="text-sm text-muted-foreground py-4 text-center">No service types in this category.</p>
          )}
        </div>
      )}
      <Button variant="ghost" size="sm" className="mt-4" onClick={onCancel}>Cancel</Button>
    </div>
  );
};

// ── Main page ────────────────────────────────────────────────────────────────
const ProviderServicesPage = () => {
  const { user } = useAuth();
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();

  const [services, setServices] = useState([]);
  const [allSubcategories, setAllSubcategories] = useState([]); // for resolving names
  const [dialogOpen, setDialogOpen] = useState(false);
  const [remapTarget, setRemapTarget] = useState(null); // service id being remapped
  const [loading, setLoading] = useState(true);
  const [commissionRate, setCommissionRate] = useState(2.5);
  const [newService, setNewService] = useState({
    subcategory_id: '',
    subcategory_name: null,  // { en, es, fr }
    category_name: null,
    service_name: { es: '', en: '', fr: '' },
    description: { es: '', en: '', fr: '' },
    rate: 0,
    unit: 'hour',
  });
  const [pickerVisible, setPickerVisible] = useState(false);

  const fetchServices = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/provider/services`);
      setServices(res.data);
    } catch {
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSubcategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/subcategories`);
      setAllSubcategories(res.data);
    } catch {
      // non-fatal: names just won't resolve for category path display
    }
  }, []);

  const fetchCommissionRate = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/commission-config`);
      setCommissionRate(res.data.rate);
    } catch {
      setCommissionRate(2.5);
    }
  }, []);

  useEffect(() => {
    if (user?.role !== 'provider') { navigate('/dashboard'); return; }
    fetchServices();
    fetchSubcategories();
    fetchCommissionRate();
  }, [user, navigate, fetchServices, fetchSubcategories, fetchCommissionRate]);

  // Build a lookup map for subcategory and parent category name resolution
  // Key by BOTH s.id (underscore) and s.slug (hyphen) to handle either stored form
  const subMap = {};
  allSubcategories.forEach(s => {
    if (s.id) subMap[s.id] = s;
    if (s.slug && s.slug !== s.id) subMap[s.slug] = s;
  });

  const resolveServiceCategory = (service) => {
    const sub = subMap[service.category_id];
    if (!sub) return null; // legacy / unresolved
    return {
      subcategoryName: resolveName(sub.name, i18n.language),
      categoryName: resolveName(sub.category_name || {}, i18n.language),
    };
  };

  const handlePickerSelect = ({ category, subcategory }) => {
    setNewService(prev => ({
      ...prev,
      subcategory_id: subcategory.slug ?? subcategory.id,   // prefer slug; fall back to id
      subcategory_name: subcategory.name,
      category_name: category.name,
    }));
    setPickerVisible(false);
  };

  const handleRemapSelect = async ({ subcategory }) => {
    if (!remapTarget) return;
    try {
      await axios.put(`${API}/provider/services/${remapTarget}`, { category_id: subcategory.slug ?? subcategory.id });
      toast.success('Service updated');
      setRemapTarget(null);
      fetchServices();
      fetchSubcategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update service');
    }
  };

  const addService = async () => {
    if (!newService.subcategory_id) { toast.error(t('service.select_subcategory')); return; }
    try {
      await axios.post(`${API}/provider/services`, {
        category_id: newService.subcategory_id, // backend stores subcategory slug here
        service_name: newService.service_name,
        description: newService.description,
        rate: newService.rate,
        unit: newService.unit,
      });
      toast.success('Service added successfully!');
      setDialogOpen(false);
      setNewService({ subcategory_id: '', subcategory_name: null, category_name: null, service_name: { es: '', en: '', fr: '' }, description: { es: '', en: '', fr: '' }, rate: 0, unit: 'hour' });
      setPickerVisible(false);
      await fetchServices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add service');
    }
  };

  const deleteService = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    try {
      await axios.delete(`${API}/provider/services/${serviceId}`);
      toast.success('Service deleted');
      await fetchServices();
    } catch {
      toast.error('Failed to delete service');
    }
  };

  const updateServiceField = (field, value) => {
    if (field === 'service_name' || field === 'description') {
      setNewService(prev => ({ ...prev, [field]: { es: value, en: value, fr: value } }));
    } else {
      setNewService(prev => ({ ...prev, [field]: value }));
    }
  };

  const calculateEarnings = (rate) => (rate * (1 - commissionRate / 100)).toFixed(2);

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;

  return (
    <div className="min-h-screen bg-background">
      <nav className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <Button variant="ghost" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Outfit' }}>My Services</h1>
            <p className="text-muted-foreground">Platform fee: {commissionRate}%</p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setPickerVisible(false); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-service-button">
                <Plus className="w-4 h-4 mr-2" />
                Add Service
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Add New Service</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {/* Category / subcategory picker */}
                <div>
                  <Label>Service Type</Label>
                  {newService.subcategory_id && !pickerVisible ? (
                    <div className="flex items-center gap-2 mt-1 p-3 border rounded-lg bg-gray-50">
                      <div className="flex-1 text-sm">
                        <span className="text-muted-foreground">{resolveName(newService.category_name, i18n.language)}</span>
                        {newService.category_name && <ChevronRight className="w-3.5 h-3.5 inline mx-1 text-muted-foreground" />}
                        <span className="font-medium">{resolveName(newService.subcategory_name, i18n.language)}</span>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setPickerVisible(true)}>Change</Button>
                    </div>
                  ) : (
                    <div className="mt-1 p-3 border rounded-lg">
                      {pickerVisible || !newService.subcategory_id ? (
                        <CategorySubcategoryPicker
                          onSelect={handlePickerSelect}
                          onCancel={() => setPickerVisible(false)}
                        />
                      ) : (
                        <Button variant="outline" size="sm" onClick={() => setPickerVisible(true)}>
                          {t('service.select_subcategory')}
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <Label>Service Name</Label>
                  <Input
                    value={newService.service_name[i18n.language]}
                    onChange={e => updateServiceField('service_name', e.target.value)}
                    placeholder="e.g., Deep House Cleaning"
                  />
                </div>

                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={newService.description[i18n.language]}
                    onChange={e => updateServiceField('description', e.target.value)}
                    placeholder="Describe what's included..."
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Rate (USD)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newService.rate}
                      onChange={e => updateServiceField('rate', parseFloat(e.target.value))}
                    />
                  </div>
                  <div>
                    <Label>Unit</Label>
                    <Select value={newService.unit} onValueChange={v => updateServiceField('unit', v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hour">Hour</SelectItem>
                        <SelectItem value="day">Day</SelectItem>
                        <SelectItem value="job">Job</SelectItem>
                        <SelectItem value="sqm">Square Meter</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {newService.rate > 0 && (
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm text-muted-foreground">Your Rate</div>
                        <div className="text-2xl font-bold">${newService.rate}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm text-muted-foreground">Platform Fee ({commissionRate}%)</div>
                        <div className="text-xl font-semibold text-red-600">
                          -${(newService.rate * commissionRate / 100).toFixed(2)}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-muted-foreground">You Earn</div>
                        <div className="text-2xl font-bold" style={{ color: 'hsl(120 100% 30%)' }}>
                          ${calculateEarnings(newService.rate)}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <Button
                  onClick={addService}
                  className="w-full"
                  disabled={!newService.subcategory_id || !newService.rate}
                >
                  Add Service
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Remap dialog for legacy/unresolved services */}
        {remapTarget && (
          <Dialog open={!!remapTarget} onOpenChange={() => setRemapTarget(null)}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Update Service Category</DialogTitle>
              </DialogHeader>
              <CategorySubcategoryPicker
                onSelect={handleRemapSelect}
                onCancel={() => setRemapTarget(null)}
              />
            </DialogContent>
          </Dialog>
        )}

        {services.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              No services added yet. Click "Add Service" to get started.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {services.map((service) => {
              const resolved = resolveServiceCategory(service);
              const isLegacy = !resolved;

              return (
                <Card key={service.id} data-testid={`service-${service.id}`}>
                  <CardContent className="pt-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <h3 className="text-xl font-bold">
                            {service.service_name[i18n.language] || service.service_name.es}
                          </h3>
                          {isLegacy ? (
                            <span className="flex items-center gap-1 text-xs bg-yellow-50 text-yellow-700 border border-yellow-200 px-2 py-0.5 rounded-full">
                              <AlertCircle className="w-3 h-3" />
                              {t('catalog.uncategorized_service')}
                            </span>
                          ) : (
                            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                              {resolved.categoryName && `${resolved.categoryName} › `}{resolved.subcategoryName}
                            </span>
                          )}
                        </div>

                        {isLegacy && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="mb-3"
                            onClick={() => setRemapTarget(service.id)}
                          >
                            Update category
                          </Button>
                        )}

                        <p className="text-sm text-muted-foreground mb-4">
                          {service.description[i18n.language] || service.description.es}
                        </p>

                        <div className="grid grid-cols-4 gap-4 bg-gray-50 p-4 rounded-lg">
                          <div>
                            <div className="text-xs text-muted-foreground mb-1">Your Rate</div>
                            <div className="text-lg font-bold">${service.rate}</div>
                            <div className="text-xs text-muted-foreground">per {service.unit}</div>
                          </div>
                          <div>
                            <div className="text-xs text-muted-foreground mb-1">Platform Fee</div>
                            <div className="text-lg font-semibold text-red-600">-${service.commission_amount}</div>
                            <div className="text-xs text-muted-foreground">{commissionRate}%</div>
                          </div>
                          <div>
                            <div className="text-xs text-muted-foreground mb-1">You Earn</div>
                            <div className="text-lg font-bold" style={{ color: 'hsl(120 100% 30%)' }}>
                              ${service.earnings_per_unit}
                            </div>
                            <div className="text-xs text-muted-foreground">per {service.unit}</div>
                          </div>
                          <div className="flex items-center justify-end">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => deleteService(service.id)}
                              className="text-red-600 hover:text-red-700"
                              data-testid={`delete-${service.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProviderServicesPage;
